import { ExternalLink, FileDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { buildPrintPDF, TooManyPages, type Printable } from '@/lib/print/build'
import { coverSize, FORMATS, type CoverSize } from '../../../shared/print'

// Making a book's print files when asked, in the browser, and handing them over: a PDF of the pages to print anywhere
// (`formatId` 'pdf'), or a bound book's interior and cover, the two files Lulu asks for, with how to order it there.
// Ordering inside the app is on hold, so for now the book is ordered on Lulu's own site, which takes payment. Give it
// a new key when the book or the choice changes, so files made for the old one aren't offered.

// A few names, then how many more.
const listed = (names: string[]) =>
  names.length <= 4 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`

type File = { name: string; label: string; url: string }
type Made = { files: File[]; pages: number; added: number; minPages: number; binding?: string; lowRes: string[] }

export function MakeFiles({
  book,
  formatId,
  filler,
  part = 'print',
  describe = (pages) => `The PDF has ${pages} pages.`,
  onBusy,
}: {
  // Only a bound book needs a cover; a PDF to print anywhere is its pages alone.
  book: Printable | Omit<Printable, 'cover'>
  formatId: string
  // What would fill the pages added for notes ("More photos").
  filler: string
  // The PDF's file name ends in this ("-print.pdf"), and says what it holds.
  part?: string
  describe?: (pages: number) => string
  onBusy?: (busy: boolean) => void
}) {
  const [state, setState] = useState<{ busy: boolean; made?: Made; error?: Error }>({ busy: false })
  // The files live in this tab only while they're offered.
  useEffect(() => () => state.made?.files.forEach((x) => URL.revokeObjectURL(x.url)), [state.made])
  useEffect(() => onBusy?.(state.busy), [state.busy, onBusy])
  const how = formatId === 'pdf' ? 'pdf' : 'bound'

  async function make() {
    setState({ busy: true })
    const url = (bytes: Uint8Array) => URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }))
    try {
      const { pdfName } = await import('@/lib/print/kit')
      const format = FORMATS.find((x) => x.id === formatId)
      if (how === 'pdf' || !format || !('cover' in book)) {
        const pdf = await book.interior()
        setState({
          busy: false,
          made: {
            files: [{ name: pdfName(book.title, part), label: 'Download the PDF', url: url(pdf.bytes) }],
            pages: pdf.pages,
            added: 0,
            minPages: 0,
            lowRes: pdf.lowRes,
          },
        })
        return
      }
      // The printer's own cover size when it's connected; otherwise the size its published rules give.
      const made = await buildPrintPDF(book, format, {
        coverSize: async (pages) => {
          const { size } = await api<{ size: CoverSize | null }>(
            `/print/cover-size?format=${encodeURIComponent(format.id)}&pages=${pages}`,
          )
          return size ?? coverSize(format, pages)
        },
      })
      const binding = format.binding
      setState({
        busy: false,
        made: {
          files: [
            {
              name: pdfName(book.title, `${binding}-interior`),
              label: 'Download the interior',
              url: url(made.interior),
            },
            { name: pdfName(book.title, `${binding}-cover`), label: 'Download the cover', url: url(made.cover) },
          ],
          pages: made.pages,
          added: made.added,
          minPages: format.minPages,
          binding: format.binding,
          lowRes: made.lowRes,
        },
      })
    } catch (e) {
      console.error(e)
      setState({
        busy: false,
        error: new Error(e instanceof TooManyPages ? e.message : 'The print files couldn’t be made. Please try again.'),
      })
    }
  }

  const made = state.made
  const format = FORMATS.find((x) => x.id === formatId)
  return (
    <div className="flex flex-col gap-4">
      {!made && (
        <Button size="lg" className="self-start" disabled={state.busy} onClick={make}>
          {state.busy ? 'Making the files…' : how === 'pdf' ? 'Make the PDF' : 'Make the files'}
        </Button>
      )}
      <FormError error={state.error ?? null} />

      {made && (
        <div className="flex flex-col gap-4" aria-live="polite">
          <p>
            {made.binding ? `The ${made.binding} has ${made.pages} pages.` : describe(made.pages)}
            {made.added > 0 &&
              ` A ${made.binding} needs at least ${made.minPages}, so ${made.added} ruled ${
                made.added === 1 ? 'page' : 'pages'
              } for notes ${made.added === 1 ? 'was' : 'were'} added at the end. ${filler} would fill ${
                made.added === 1 ? 'it' : 'them'
              } instead.`}
          </p>
          {made.lowRes.length > 0 && (
            <p className="rounded-2xl bg-secondary px-4 py-3 text-sub">
              {made.lowRes.length === 1 ? 'This photo is' : 'These photos are'} small for a printed page and may look
              soft: {listed(made.lowRes)}. A larger photo would print sharper.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {made.files.map((file) => (
              <Button key={file.name} asChild size="lg" variant={file === made.files[0] ? 'default' : 'outline'}>
                <a href={file.url} download={file.name}>
                  <FileDown aria-hidden /> {file.label}
                </a>
              </Button>
            ))}
          </div>
          {made.binding && (
            <div className="flex flex-col gap-3 rounded-2xl border border-input px-4 py-3 text-sub">
              <p className="font-medium">To order it from Lulu</p>
              <ol className="ml-4 list-decimal space-y-1 text-muted-foreground">
                <li>Download the interior and the cover.</li>
                <li>
                  On Lulu’s website, start a print book: US Letter, 8.5 × 11 in, {format!.binding}, and upload the two
                  files.
                </li>
                <li>Lulu shows the price, takes payment and mails the book to you.</li>
              </ol>
              <Button asChild variant="outline" className="self-start">
                <a href="https://www.lulu.com/" target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden /> Open Lulu
                </a>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
